/**
 * /notifications/$id — single notification view.
 *
 * Marks the notification as read on mount (if not already). The body is
 * rendered as plain text — the rich HTML body lives in the email channel
 * and isn't reused here to keep XSS off the table.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { notificationsApi } from '@/lib/api';
import { lastSearch } from '@/lib/last-search';
import { queryKeys } from '@/lib/query-keys';

export const Route = createFileRoute('/_authenticated/notifications/$id')({
  component: Detail,
});

function Detail() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.notifications.detail(id),
    queryFn: () => notificationsApi.get(id),
  });

  const markRead = useMutation({
    mutationFn: (nid: string) => notificationsApi.markRead(nid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });

  // Idempotent: server is a no-op if already read.
  useEffect(() => {
    if (data?.data && !data.data.readAt) {
      markRead.mutate(data.data.id);
    }
  }, [data?.data?.id]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-3 p-6">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="mx-auto max-w-3xl space-y-3 p-6">
        <Link to="/notifications" search={lastSearch('/notifications')}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-1" /> Back to inbox
          </Button>
        </Link>
        <p className="text-muted-foreground">Notification not found.</p>
      </div>
    );
  }

  const n = data.data;
  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      <Link to="/notifications" search={lastSearch('/notifications')}>
        <Button variant="ghost" size="sm">
          <ArrowLeft className="mr-1" /> Back to inbox
        </Button>
      </Link>
      <div>
        <h1 className="font-semibold text-2xl">{n.title}</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          {new Date(n.createdAt).toLocaleString()} · {n.templateName}
        </p>
      </div>
      <p className="whitespace-pre-wrap text-base leading-relaxed">{n.body}</p>
    </div>
  );
}
